import { useEffect, useState } from 'react'
import {
    Box,
    Button as MuiButton,
    Link,
    Typography,
    useTheme,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { useTranslation } from 'react-i18next'
import { useActivateNow } from '../ListingPage/useRequestAccessResources'
import { useAccessRequestPolicy, useTargetPerson } from 'hooks'
import { Spinner, Tooltip } from 'packages/eid-ui'
import { Modal } from 'components/Modal'
import { Icon } from 'packages/eid-icons'
import { ItemDetails } from 'components'
import { useNotification } from 'packages/eid-ui'
import { useHistory } from 'react-router'
import config from 'config'
import GenericButton from 'components/Button/GenericButton'
import { getURLTarget } from 'packages/core'
import { ActivateConfirmModal } from 'components/ActivateConfirmModal'
import { useTimeConstrainedControl } from 'components/TimeConstrainedControl'

const drawerButtonStyles = makeStyles((theme) => ({
    root: {
        minHeight: '31px',
        border: `solid 1px ${theme?.palette?.secondary?.main}`,
        backgroundColor: theme?.palette?.secondary?.main,
        boxShadow: '0 0 0 0',
        color: theme?.palette?.common?.white,
        textTransform: 'capitalize !important',
        borderRadius: '16px',
        lineHeight: '1.3',
        '&:hover': {
            backgroundColor: theme?.palette?.secondary?.main,
        },
    },
}))

const ActivateNowButton = ({
    resourceType,
    resource,
    forDetailsDrawer = false,
    forCardView = false,
    assignment = null,
    width = '',
    resourceId = '',
    resourceAssignmentId = '',
    locationId = '',
    policyId = '',
    ...rest
}) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const history = useHistory()
    const { showWarningMessage } = useNotification()
    const [targetPerson] = useTargetPerson()

    const showActivateConfirmModal =
        String(config.SHOW_ACTIVATE_NOW_DURATION_SELECTION) === 'true'

    const [showModal, setShowModal] = useState(false)
    const [showConfirmModal, setShowConfirmModal] = useState(false)

    const [activateResource, { data, isError, isLoading }] = useActivateNow()

    // Fetch policy using the policyId prop or resource.requestPolicyId
    const effectivePolicyId = policyId || resource?.requestPolicyId || ''
    const accessRequestPolicy = useAccessRequestPolicy(effectivePolicyId)

    // Time constraint controls for inline rendering (detail drawer)
    // When policy requires time constraints, force hideSwitch to show the control without the toggle
    const shouldHideSwitch = accessRequestPolicy?.isTimeConstrained ?? false
    const {
        timeConstrained,
        selectedStartDate,
        selectedEndDate,
        hasInvalidDates,
        violatesPolicy,
        timeConstrainedControl,
    } = useTimeConstrainedControl(
        accessRequestPolicy,
        shouldHideSwitch,
        false,
        shouldHideSwitch,
        false,
    )

    useEffect(() => {
        if (isError) {
            showWarningMessage(t('FailedToActivateNow'))
        } else if (data) {
            setShowModal(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isError, isLoading])

    const classes = drawerButtonStyles()

    const handleActivateNow = () => {
        if (showActivateConfirmModal) {
            if (!forDetailsDrawer) {
                setShowConfirmModal(true)
            } else {
                handleActivateWithInlineControls()
            }
        } else {
            handleConfirmActivate({
                timeConstrained: false,
                startDateUtc: null,
                endDateUtc: null,
            })
        }
    }

    const handleActivateWithInlineControls = () => {
        // If policy requires time constraints, always include them
        const isTimeConstrainedRequired =
            accessRequestPolicy?.isTimeConstrained ?? false
        const shouldIncludeTimeConstraint =
            timeConstrained || isTimeConstrainedRequired

        const timeConstraintDetails = {
            timeConstrained: shouldIncludeTimeConstraint,
            startDateUtc: shouldIncludeTimeConstraint
                ? selectedStartDate.utc().format()
                : null,
            endDateUtc: shouldIncludeTimeConstraint
                ? selectedEndDate?.utc().format()
                : null,
        }
        const dataObject = {
            resourceType: resourceType,
            resourceId: resourceId || resource.id,
            targetPersonPersonId: targetPerson.id,
            resourceAssignmentId: resourceAssignmentId.length
                ? resourceAssignmentId
                : assignment?.resourceAssignment?.resourceAssignmentId
                ? assignment.resourceAssignment.resourceAssignmentId
                : resource?.resourceAssignmentId,
            locationId: locationId,
            timeConstraintDetails: timeConstraintDetails,
        }
        activateResource(dataObject)
    }

    const handleConfirmActivate = (timeConstraintDetails) => {
        const dataObject = {
            resourceType: resourceType,
            resourceId: resourceId || resource.id,
            targetPersonPersonId: targetPerson.id,
            resourceAssignmentId: resourceAssignmentId.length
                ? resourceAssignmentId
                : assignment?.resourceAssignment?.resourceAssignmentId
                ? assignment.resourceAssignment.resourceAssignmentId
                : resource?.resourceAssignmentId,
            locationId: locationId,
            timeConstraintDetails: timeConstraintDetails,
        }
        activateResource(dataObject)
        setShowConfirmModal(false)
    }

    const getTooltipTitle = () => {
        if (hasInvalidDates) {
            return t('Common_InvalidDates')
        }
        if (violatesPolicy) {
            return t('Common_PolicyAllowsMaxDurationInMinError', {
                count: accessRequestPolicy?.maximumValueInMinutes,
            })
        }
        return ''
    }

    const getManageAccessUrl = () => {
        return `${config.APP_SUBPATH}${resourceType?.route}/manageAccess?selected=${resource.id}`
    }
    return (
        <>
            {showActivateConfirmModal && (
                <ActivateConfirmModal
                    open={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    onConfirm={handleConfirmActivate}
                    resourceName={resource.friendlyName || resource.name || ''}
                    policyId={effectivePolicyId}
                    proceeding={isLoading}
                />
            )}
            <Modal
                open={showModal}
                onClose={() => setShowModal(false)}
                title={
                    <>
                        <p>
                            <Icon
                                name="CheckedRound"
                                style={{ marginRight: '10px' }}
                            />
                            {resource.friendlyName} {t('ActivatedSoonMessage')}.
                        </p>
                        {history.location.pathname.indexOf(
                            `${config.APP_SUBPATH}${resourceType?.route}/manageAccess`,
                        ) < 0 ? (
                            <p>
                                {t('CheckExistingAccess')}{' '}
                                <Link
                                    underline="hover"
                                    target={getURLTarget()}
                                    href={getManageAccessUrl()}
                                >
                                    {t('Here')}
                                </Link>
                                .
                            </p>
                        ) : null}
                    </>
                }
            />
            {forCardView && (
                <GenericButton
                    onClick={handleActivateNow}
                    loading={isLoading}
                    color={theme?.palette?.primary?.main}
                    rootStylesProp={{
                        borderRadius: '4px',
                        width: width ? width : '120px',
                        height: '32px',
                        fontSize: '14px',
                        padding: '8px 12px 8px 12px',
                    }}
                    label={t('Activate')}
                />
            )}
            {forDetailsDrawer ? (
                <Box
                    sx={{
                        display: 'flex',
                        marginBottom: '16px',
                        marginTop: '40px',
                        padding: '0 31px',
                        [theme.breakpoints.up('sm')]: {
                            height: '80px',
                            alignItems: 'center',
                            position: 'relative',
                        },
                        [theme.breakpoints.down('md')]: {
                            flexDirection: 'column-reverse',
                            paddingTop: '16px !important',
                            paddingBottom: '16px !important',
                        },
                    }}
                    {...rest}
                >
                    <Tooltip
                        title={
                            showActivateConfirmModal ? getTooltipTitle() : ''
                        }
                    >
                        <Box minWidth="167px">
                            <ItemDetails.ActionButton
                                fontColor={theme?.palette?.common?.white}
                                bgColor={theme?.palette?.secondary?.main}
                                width="fit-content"
                                minWidth="100%"
                                loading={isLoading}
                                disabled={
                                    showActivateConfirmModal &&
                                    (hasInvalidDates || violatesPolicy)
                                }
                                onClick={handleActivateNow}
                            >
                                <Box display="flex" alignItems="center">
                                    <Icon
                                        name="Check"
                                        color={theme?.palette?.common?.white}
                                    />
                                </Box>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    marginLeft="10px"
                                >
                                    <Typography
                                        style={{
                                            color: theme?.palette?.common
                                                ?.white,
                                        }}
                                    >
                                        {t('Activate')}
                                    </Typography>
                                </Box>
                            </ItemDetails.ActionButton>
                        </Box>
                    </Tooltip>
                    <Box
                        sx={{
                            [theme.breakpoints.up('sm')]: {
                                display: 'flex',
                                marginLeft: '40px',
                                alignItems: 'center',
                            },
                            [theme.breakpoints.down('md')]: {
                                paddingBottom: '16px',
                            },
                        }}
                    >
                        {showActivateConfirmModal && timeConstrainedControl}
                    </Box>
                </Box>
            ) : (
                <>
                    {!forCardView && (
                        <MuiButton
                            size="small"
                            classes={classes}
                            variant="outlined"
                            onClick={handleActivateNow}
                            {...rest}
                            endIcon={
                                <Icon
                                    name="ArrowSmall"
                                    fill="white"
                                    color="white"
                                    direction="right"
                                />
                            }
                        >
                            {isLoading ? (
                                <Spinner
                                    color={theme?.palette?.common?.white}
                                />
                            ) : (
                                t('Activate')
                            )}
                        </MuiButton>
                    )}
                </>
            )}
        </>
    )
}
export default ActivateNowButton
